import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment';
import _ from 'lodash';

import withChartExport from '../../../components/ChartExporting';
import dateUtils, { getWorkMinutesPerDay, MINUTES_PER_DAY } from '../../../util/dates';
import { isWhite } from '../../../util/colors';
import math from '../../../util/math';
import { getDetailsData } from '../CTHActions';
import dataUtils from '../../../util/dataUtils';
import { escapeHTML } from '../../../util/text';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

const chartConfig = {
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: null,
    type: 'column',
    zoomType: 'xy',
    spacingBottom: 10,
    marginRight: 10,
    marginLeft: 70,
    events: {
      load: e => {
        e.target.options.exporting.sourceHeight = e.target.chartHeight;
        e.target.options.exporting.sourceWidth = e.target.chartWidth;
      }
    }
  },
  title: {
    text: ''
  },
  xAxis: {
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    title: {
      text: 'Cycle time (Days)',
      style: {
        fontSize: '13px'
      }
    },
    plotLines: [],
    events: {}
  },
  yAxis: {
    allowDecimals: false,
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    min: 0,
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Frequency (Number of work items)',
      style: {
        fontSize: '13px'
      }
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false,
      color: 'rgba(43,147,185, 0.9)',
      states: {
        hover: {
          color: 'rgba(0,123,170, 0.9)'
        }
      },
      point: {
        events: {}
      }
    },
    column: {
      pointPadding: 0,
      borderWidth: 0.5,
      groupPadding: 0,
      shadow: false
    }
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0,
    formatter: function() {}
  },
  series: [
    {
      name: 'Data',
      data: []
    }
  ]
};

const masterchartConfig = {
  credits: {
    enabled: false
  },
  exporting: {
    enabled: false
  },
  chart: {
    reflow: false,
    borderWidth: 0,
    backgroundColor: null,
    marginLeft: 70,
    marginRight: 10,
    zoomType: 'xy',
    type: 'scatter',
    events: {}
  },
  title: {
    text: ''
  },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    showLastTickLabel: true,
    plotBands: [
      {
        id: 'mask-before',
        color: 'rgba(0, 0, 0, 0.1)'
      }
    ],
    title: {
      text: null
    },
    labels: {
      style: {
        fontSize: '12px'
      }
    }
  },
  yAxis: {
    gridLineColor: '#F5F5F5',
    gridLineWidth: 1,
    title: {
      text: 'Cycle time (days)',
      style: {
        fontSize: '13px'
      }
    },
    labels: {
      formatter: function() {
        return Math.round(this.value / MINUTES_PER_DAY);
      },
      style: {
        fontSize: '12px'
      }
    }
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    scatter: {
      marker: {
        symbol: 'circle',
        radius: 5
      }
    }
  },
  tooltip: {
    formatter: function() {
      return false;
    }
  },
  legend: {
    enabled: false
  },
  series: [
    {
      name: 'Data'
    }
  ]
};

export class CTHChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};

    this.onSelection = this.onSelection.bind(this);
    this.updateDetails = this.updateDetails.bind(this);
  }

  static defaultProps = {
    onPointClick: function() {}
  };

  calculatePercentiles = data => {
    const lines = [],
      ticks = [];
    let bars = 0;

    if (!!data.series) {
      const last = _.last(data.series);
      bars = last ? last.x + 1 : 0;
    }

    this.calcMeanModeMedian(lines, ticks, data);

    const workMinutesPerDay = getWorkMinutesPerDay(this.props.excludeWeekendOpts, this.props.useWorkingHoursInCycleTimeDays);
    for (let i = 0; i < this.props.percentiles.length; i++) {
      const pObj = _.find(data.percentiles, item => item.name == this.props.percentiles[i]);
      if (!pObj) {
        continue;
      }

      const text = `${pObj.name}%`;
      const value = this.props.timeUnit === 'day'
        ? Math.ceil(pObj.rawValue / workMinutesPerDay)
        : Math.ceil(moment.duration(pObj.rawValue, 'minutes').as(this.props.timeUnit));
      this.addLine(lines, text, value);
      this.addTick(ticks, text, value);
    }

    const newLines = [];
    let lastValue = 0;
    let coll = _(lines)
      .groupBy('value')
      .toPairs()
      .orderBy(l => {
        return +l[0];
      })
      .value();
    _.forEachRight(coll, l => {
      const line = l[1][0];
      const labels = _.map(l[1], l => {
        return l.label.text;
      });
      line.label.text = labels.join(', ');

      const diff = lastValue - +l[0];
      if (diff > 0 && diff < bars * 0.035) {
        line.label.x = -13;
      } else {
        line.label.x = 4;
      }

      newLines.push(line);
      lastValue = +l[0];
    });

    const newTicks = [];
    lastValue = 0;
    coll = _(ticks)
      .groupBy('value')
      .toPairs()
      .orderBy(l => {
        return +l[0];
      })
      .value();
    _.forEachRight(coll, t => {
      const tick = t[1][0];
      if (tick.label) {
        const diff = lastValue - +t[0];
        if (diff > 0 && diff < bars * 0.035) {
          tick.label.x = -20;
        } else {
          tick.label.x = -6;
        }
      }
      if (t[1][1]) {
        const labels = _(t[1])
          .map(l => {
            return l.text;
          })
          .filter(l => !!l)
          .value();
        t[1][1].events = highchartUtils.plotlinesTooltip(labels.join(', '));
        newTicks.push(t[1][1]);
      }

      newTicks.push(tick);
      lastValue = +t[0];
    });

    return newLines.concat(newTicks);
  };

  calcMeanModeMedian(lines, ticks, data) {
    if (!data.series || !data.series.length) {
      return false;
    }

    const mode = math.getModeFromHistogramBy(data.series, 'y', 'x');
    const mean = math.getMeanFromCTHistogramBy(data.series, 'y', 'ct');
    const median = math.getMedianFromHistogramBy(data.series, 'y', 'x');

    this.addLine(lines, 'Mean', mean < 1 ? (mean/2 + 0.5).toFixed(2) : mean.toFixed(2));
    this.addLine(lines, 'Median', median);
    this.addLine(lines, 'Mode', mode);

    this.addTick(ticks, 'Mean', mean < 1 ? (mean/2 + 0.5).toFixed(2) : mean.toFixed(2), mean.toFixed(2));
    this.addTick(ticks, 'Median', median);
    this.addTick(ticks, 'Mode', mode);
  }

  addLine(lines, text, value) {
    lines.push({
      color: '#444444',
      dashStyle: 'dot',
      zIndex: 2,
      width: 2,
      value,
      label: {
        verticalAlign: 'top',
        rotation: 90,
        text,
        style: {
          ...highchartUtils.getFontSizeConfig('percentile', this.props.wallmode)
          //color: '#ff0000',
        }
      }
    });
  }

  addTick(ticks, text, value, label) {
    ticks.push({
      color: '#444444',
      dashStyle: 'dot',
      width: 2,
      zIndex: 1,
      value,
      label: {
        verticalAlign: 'bottom',
        textAlign: 'center',
        rotation: 0,
        x: -6,
        y: 20.5,
        text: label && `${label}` || value && `${value}`,
        useHTML: true,
        style: {
          ...highchartUtils.getFontSizeConfig('percentile', this.props.wallmode),
          padding: '0 7px',
          backgroundColor: '#fcfcfc',
          color: '#505050',
          fontWeight: 'bold'
        }
      }
    });
    ticks.push({
      name: 'tooltip',
      color: 'transparent',
      width: 4,
      zIndex: 2,
      value,
      text
    });
  }

  componentDidMount = () => {
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    chartConfig.exporting = this.props.chartExporting;
    masterchartConfig.chart.events.selection = this.onSelection;

    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(that, this);
    };
    chartConfig.plotOptions.series.point.events.click = function() {
      that.onPointClick(that, this);
    };

    this.master = new Highcharts['Chart'](`${this.props.id}-master`, masterchartConfig);
    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    this.updateTimeLabelsFormat(this.props.dateFormat);
    this.updateYAxisFormatter();

    if (this.props.data.series) {
      this.updateChart();
    }

    if (this.props.wallmode) {
      this.updateChartFont();
    }
  };

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.data, this.props.data)) {
      this.updateChart(prevProps.data);
    }

    if (this.props.data.series && prevProps.details != this.props.details) {
      this.updateDetails(this.props.details);
    }

    if (prevProps.percentiles != this.props.percentiles) {
      this.chart.update({
        xAxis: {
          plotLines: this.calculatePercentiles(this.props.details)
        }
      });
    }

    if (prevProps.timeUnit !== this.props.timeUnit) {
      this.props.getDetailsData(this.props.dates);
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }

    if (!_.isEqual(prevProps.excludeWeekendOpts, this.props.excludeWeekendOpts)) {
      this.updateYAxisFormatter();
    }

    if (prevProps.showNavigator != this.props.showNavigator) {
      this.master.reflow();
      this.chart.reflow();
    }

    if (prevProps.chartExporting != this.props.chartExporting) {
      this.chart.update(
        {
          exporting: {
            filename: this.props.chartExporting.filename,
            chartOptions: {
              title: {
                text: 'Cycle Time Histogram',
                style: {
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              },          
            }
          }
        },
        false
      );
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
    this.master.destroy();
  }

  updateChart(oldData = {}) {
    this.master.update(
      {
        xAxis: {
          plotBands: [
            {
              id: 'mask-before',
              color: 'rgba(0, 0, 0, 0.2)'
            }
          ]
        }
      },
      false
    );

    const diff = dataUtils.getDiff(oldData.series, this.props.data.series, 'data');
    highchartUtils.updateSeries(diff, this.master);
    this.master.redraw();

    if (this.props.details.series) {
      this.updateDetails(this.props.details);
    }
  }

  updateTimeLabelsFormat(dateFormat) {
    const config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.master.update(config);
  }

  updateYAxisFormatter() {
    const workMinutesPerDay = getWorkMinutesPerDay(this.props.excludeWeekendOpts, this.props.useWorkingHoursInCycleTimeDays);
    this.master.update({
      yAxis: {
        labels: {
          formatter: function() {
            return Math.ceil(this.value / workMinutesPerDay);
          }
        }
      }
    });
  }

  updateDetails(data) {
    this.chart.update(
      {
        xAxis: {
          title: {
            text: `Cycle time (${this.props.timeUnit}s)`
          },
          plotLines: this.calculatePercentiles(data)
        },
        series: [
          {
            data: data.series
          }
        ]
      },
      true
    );
  }

  updateChartFont() {
    const newConfig = highchartUtils.getFontSizeConfig('cth', this.props.wallmode);
    this.chart.update(newConfig.chart);
    this.master.update(newConfig.master);
  }

  onSelection(e) {
    var extremesObject = e.xAxis[0],
      min = extremesObject.min,
      max = extremesObject.max,
      xAxis = e.target.xAxis[0];

    xAxis.removePlotBand('mask-before');
    xAxis.addPlotBand({
      id: 'mask-before',
      from: xAxis.dataMin,
      to: min,
      color: 'rgba(0, 0, 0, 0.1)',
      borderColor: 'black',
      borderWidth: 1
    });

    xAxis.removePlotBand('mask-after');
    xAxis.addPlotBand({
      id: 'mask-after',
      from: max,
      to: xAxis.dataMax,
      color: 'rgba(0, 0, 0, 0.1)',
      borderColor: 'black',
      borderWidth: 1
    });

    if (this.master.resetBtn) {
      this.master.resetBtn.destroy();
    }
    this.master.resetBtn = this.master.renderer
      .button(
        'Reset zoom',
        null,
        null,
        () => {
          this.resetExtremes();
        },
        {
          zIndex: 20
        }
      )
      .attr({
        align: 'right',
        title: 'Reset zoom level 1:1'
      })
      .add(this.master.zoomGroupButton)
      .align(
        {
          align: 'right',
          x: 5,
          y: 10
        },
        false,
        null
      );

    const from = moment(min),
      to = moment(max);
    this.props.getDetailsData({
      startDate: from,
      endDate: to
    });

    return false;
  }

  onPointClick(comp, ev) {
    let points = [];
    const frequency = `${ev.y} ${ev.y != 1 ? 'items' : 'item'}`;
    let duration = `${ev.x} ${comp.props.timeUnit}${ev.x != 1 ? 's' : ''}`;

    for (let i = 0; i < ev.cards.length; i++) {
      const point = ev.cards[i];

      points.push({
        card: point.card,
        details: point.details,
        color: point.color
      });
    }

    comp.props.onPointClick({
      frequency,
      duration,
      points
    });
  }

  tooltipFormatter(comp, ev) {
    if (ev.key == 'Plotline') {
      return `${ev.point.text}: ${ev.y} ${ev.y == 1 ? comp.props.timeUnit : comp.props.timeUnit + 's'}`;
    }

    const duration = `${ev.x} ${comp.props.timeUnit}${ev.x != 1 ? 's' : ''}`;
    const frequency = `${ev.y} ${ev.y != 1 ? 'items' : 'item'}`;
    let tooltip = `<p class="tooltip-label-size" style="margin: 8px 8px 0;">Cycle Time: ${duration}</p> <p class="tooltip-label-size" style="margin: 4px 8px 0;">Frequency: ${frequency}</p>`;

    for (let i = 0; i < ev.point.cards.length; i++) {
      const point = ev.point.cards[i];
      const isBgWhite = isWhite(point.color) ? 'border: 1px solid #00408026;' : '';
      tooltip += `<div class="tooltip-list-item"><div class="tooltip-circle-dot" style="${isBgWhite} background: ${point.color}"></div>${escapeHTML(point.card.name)}</div>`;
    }

    tooltip += `<p class="tooltip-info-txt">Click on the bar to see the details</p>`;

    return tooltip;
  }

  resetExtremes() {
    this.master.xAxis[0].removePlotBand('mask-before');
    this.master.xAxis[0].removePlotBand('mask-after');
    this.props.getDetailsData(this.props.dates);
    this.master.resetBtn.destroy();
    delete this.master.resetBtn;
  }

  render() {
    return (
      <div id={this.props.id} className="chart_content chart_tooltip_style">
        <div
          id={`${this.props.id}-details`}
          className={'chart_details ' + (!this.props.showNavigator ? 'chart_details-full ' : ' ')}
        ></div>
        <div
          style={{ display: this.props.showNavigator ? 'block' : 'none' }}
          id={`${this.props.id}-master`}
          className="chart_master"
        ></div>
      </div>
    );
  }
}

CTHChart.propTypes = {
  onPointClick: PropTypes.func
};

const mapDispatchToProps = dispatch => {
  return {
    getDetailsData: dates => dispatch(getDetailsData(dates))
  };
};

const mapStateToProps = state => {
  return {
    dates: state.filters.dates,
    dateFormat: state.filters.dateFormat,
    percentiles: state.filters.cth.selectedPercentiles,
    showNavigator: state.filters.cth.showNavigator,
    timeUnit: state.filters.cth.timeUnit,
    details: state.cth.details,
    isLoading: state.cth.isLoading,
    wallmode: state.app.wallmode,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(CTHChart));
